import type { Floor } from '@/content/floors';
import { strings } from '@/content/strings';
import type { FloorProgress } from '@/game/progress';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import { CodenameTiles } from '@/ui/CodenameTiles';
import { Frame } from '@/ui/Frame';
import { StarRating } from '@/ui/StarRating';
import { Surface } from '@/ui/Surface';
import styles from './FloorDossier.module.css';

interface FloorDossierProps {
  floor: Floor;
  progress: FloorProgress;
  onStart: () => void;
}

/**
 * The selected floor's case file (landscape): the foe, the sound context, the mission's words,
 * the stars so far with what earns the next one, and the start button.
 */
export function FloorDossier({ floor, progress, onStart }: FloorDossierProps) {
  const t = strings.elevator;
  const pictures = floor.words.filter((w) => w.kind !== 'syllable');
  return (
    <div className={styles.dossier} style={cssVars({ '--floor-color': floor.color })}>
      <Frame>
        <Surface variant="screen" className={styles.screen}>
          <div className={styles.header}>
            <span className={styles.floor}>{t.floorTitle(floor.number)}</span>
            <span className={styles.focus}>{floor.focus}</span>
          </div>
          <div className={styles.foe}>
            <img key={floor.id} src={floor.foe.portraits.disguised.url} alt="" draggable={false} />
          </div>
          <CodenameTiles
            codename={floor.foe.codename}
            revealedCount={progress.revealed}
            className={cx(styles.codename)}
          />
          <div className={styles.words} aria-label={t.practice}>
            {pictures.length > 0
              ? pictures.map((w) => (
                  <img
                    key={w.id}
                    src={w.picture.url}
                    alt={w.text}
                    title={w.text}
                    draggable={false}
                  />
                ))
              : floor.words.map((w) => (
                  <span key={w.id} className={styles.syllable}>
                    {w.text}
                  </span>
                ))}
          </div>
          <div className={styles.stars}>
            <StarRating stars={progress.stars} size={24} />
            <span className={styles.next}>{t.nextStar[progress.stars]}</span>
          </div>
          <button type="button" className={styles.start} onClick={onStart}>
            {t.start}
          </button>
        </Surface>
      </Frame>
    </div>
  );
}
